import React, { useState, useEffect, useRef, useContext } from 'react'
import {
  Card,
  Row,
  Col,
  Typography,
  Tabs,
  Spin,
  Empty
} from 'antd'
import { useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { getUsernameFromStorage, appConfig, isADUser } from '../../utils/appUtils'
import { callKolumbusUserAccess, getCachedUserAccess } from '../../api/authApiService'
import { UserContext } from '../../context/userContext'
import { StoreContext } from '../../context/storeContext'
import './home.scss'

const { Title, Text } = Typography

const Home = () => {
  const [username, setUsername] = useState('')
  const [userType, setUserType] = useState('')
  const [userAccessData, setUserAccessData] = useState(null)
  const [isLoadingUserAccess, setIsLoadingUserAccess] = useState(true)
  const [accessError, setAccessError] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [shouldValidateAccess, setShouldValidateAccess] = useState(false)
  const navigate = useNavigate()
  const { instance } = useMsal()

  // Get user context to update store access data
  const {
    setUserAccessData: setContextUserAccessData,
    setIsLoadingAccess,
    setAccessError: setContextAccessError,
    validateUserAccess,
    userAccessData: contextUserAccessData // Get existing user access data from context
  } = useContext(UserContext) || {}

  // Get store context for current store ID
  const { id: currentStoreId } = useContext(StoreContext) || {}

  // Use refs to track API calls and prevent multiple calls
  const isLoadingRef = useRef(false)
  const hasLoadedRef = useRef(false)
  const hasValidatedRef = useRef(false)

  useEffect(() => {
    setUsername(getUsernameFromStorage())
    setUserType(isADUser() ? 'Microsoft AD' : 'Cognito')

    if (contextUserAccessData && Object.keys(contextUserAccessData).length > 0 && !hasLoadedRef.current) {
      // Use existing data from context
      setUserAccessData(contextUserAccessData)
      setIsLoadingUserAccess(false)
      setIsInitialized(true)
      hasLoadedRef.current = true
      setShouldValidateAccess(true)
      return
    }

    if (!hasLoadedRef.current && !isLoadingRef.current) {
      loadUserAccessData()
    }

    // Cleanup function
    return () => {
      // Clear any pending requests when component unmounts
      if (isLoadingRef.current) {
        isLoadingRef.current = false
      }

      setAccessError(null)
    }
  }, [contextUserAccessData])

  useEffect(() => {
    if (shouldValidateAccess && userAccessData && !hasValidatedRef.current && validateUserAccess) {
      const validationTimer = setTimeout(() => {
        if (userAccessData && Object.keys(userAccessData).length > 0) {
          const hasAccess = validateUserAccess(userAccessData)
          if (!hasAccess) {
            navigate('/accessDenied')
          }
          hasValidatedRef.current = true
        }
      }, 200) // Increased delay to ensure proper state initialization

      return () => clearTimeout(validationTimer)
    }
  }, [shouldValidateAccess, userAccessData, validateUserAccess, navigate])

  const loadUserAccessData = async () => {
    // Prevent multiple simultaneous calls
    if (isLoadingRef.current || hasLoadedRef.current) {
      return
    }

    isLoadingRef.current = true
    setIsLoadingUserAccess(true)
    setAccessError(null)

    // Update context loading state
    if (setIsLoadingAccess) {
      setIsLoadingAccess(true)
    }

    try {
      // Get cached user access data first
      let cachedData = getCachedUserAccess()

      if (cachedData) {
        setUserAccessData(cachedData)
        updateUserAccessInContext(cachedData)
        setIsInitialized(true)
        hasLoadedRef.current = true
        return
      }

      // If no cached data, fetch fresh data
      const freshData = await callKolumbusUserAccess(instance)
      setUserAccessData(freshData)
      updateUserAccessInContext(freshData)
      setIsInitialized(true)
      hasLoadedRef.current = true
      setShouldValidateAccess(true)

    } catch (error) {
      const errorMessage = error.message || 'Failed to load user access data'

      const isForbiddenError = error.status === 403 ||
        error.message?.includes('403') ||
        error.message?.includes('forbidden') ||
        error.message?.includes('access denied') ||
        error.message?.includes('unauthorized') ||
        error.status === 401

      if (isForbiddenError) {
        // For 403/access denied errors, navigate immediately
        navigate('/accessDenied')
        return
      }

      // For other errors, set error state to show error message
      setAccessError(errorMessage)

      // Update context with error
      if (setContextAccessError) {
        setContextAccessError(errorMessage)
      }

      // Set userAccessData to empty object to indicate attempt was made
      setUserAccessData({})
      setIsInitialized(true)
      hasLoadedRef.current = true

    } finally {
      setIsLoadingUserAccess(false)
      isLoadingRef.current = false

      // Update context loading state
      if (setIsLoadingAccess) {
        setIsLoadingAccess(false)
      }
    }
  }

  // Update user access data in context
  const updateUserAccessInContext = (accessData) => {
    if (setContextUserAccessData) {
      setContextUserAccessData(accessData)
    }
    // Validation is now handled in separate useEffect
  }

  // Function to check if user has access to a specific app/screen
  const hasAccessToApp = (appId) => {
    if (!userAccessData || !userAccessData.message) {
      return false
    }

    const { permissions, accessibleScreens } = userAccessData.message

    // Check if app is in accessibleScreens array
    if (accessibleScreens && accessibleScreens.includes(appId)) {
      return true
    }

    // Also check permissions object for access property
    if (permissions && permissions[appId] && permissions[appId].access === true) {
      return true
    }

    return false
  }

  // Function to filter apps based on user access
  const getFilteredApps = (apps) => {
    if (!userAccessData || !userAccessData.message) {
      // If no user access data and still loading, return empty array
      if (isLoadingUserAccess || !isInitialized) {
        return []
      }
      return apps
    }

    return apps.filter(app => {
      // Special handling for apps that don't require permission checks
      const alwaysAccessibleApps = ['shelfEdgeLabels', 'savedLabels', 'productLookup', 'chatbot']

      if (alwaysAccessibleApps.includes(app.id)) {
        return true
      }

      // For Cash Management System apps, check permissions
      // Map app IDs to permission keys
      const appToPermissionMap = {
        'pettycash': 'pettyCash',
        'rfp': 'rfp',
        'vendor': 'vendor',
        'chatbot': 'aiChatbot' // if chatbot needs permission check
      }

      const permissionKey = appToPermissionMap[app.id]
      if (permissionKey) {
        return hasAccessToApp(permissionKey)
      }

      // Default: allow access if no specific permission mapping
      return true
    })
  }

  // Function to get filtered categories
  const getFilteredCategories = () => {
    // Don't filter until we're initialized
    if (!isInitialized) {
      return []
    }

    // Pass the current store ID to getAllCategories for store-based filtering
    const allCategories = appConfig.getAllCategories(currentStoreId)

    return allCategories.map(category => ({
      ...category,
      apps: getFilteredApps(category.apps)
    })).filter(category => category.apps.length > 0) // Remove empty categories
  }

  const handleAppClick = (path) => {
    navigate(path)
  }

  const renderAppCard = (app) => {
    return (
      <Card
        className="app-card"
        variant="borderless"
        styles={{ body: { padding: 0 } }}
        onClick={() => handleAppClick(app.path)}
      >
        <div className="app-card-content">
          <div className="app-icon-container" style={{ backgroundColor: app.iconBg }}>
            {app.icon}
          </div>
          <Text className="app-title">{app.dashboardTitle || app.title}</Text>
        </div>
      </Card>
    )
  }

  // Get filtered categories based on user access 
  const filteredCategories = getFilteredCategories()

  // Create tab items for Tabs component using filtered apps
  const tabItems = filteredCategories.map(category => ({
    key: category.title,
    label: category.title,
    children: category.apps.length > 0 ? (
      <Row gutter={[16, 16]} className="apps-grid">
        {category.apps.map(app => (
          <Col xs={12} sm={8} md={6} lg={4} xl={3} key={app.id}>
            {renderAppCard(app)}
          </Col>
        ))}
      </Row>
    ) : (
      <Empty
        description="No accessible apps in this category"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    )
  }))

  // Show loading spinner while user access data is being fetched or not initialized
  if (isLoadingUserAccess || !isInitialized) {
    return (
      <div className="home-container">
        <div className="home-header">
          <Title level={4} className="home-title">Store dashboard</Title>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <Spin size="large" />
          <Text type="secondary">Loading user access data...</Text>
        </div>
      </div>
    )
  }

  // Show message if there was an error loading access data
  if (accessError) {
    return (
      <div className="home-container">
        <div className="home-header">
          <Title level={4} className="home-title">Store dashboard</Title>
          <div className="header-info">
            {username && (
              <Text className="welcome-text">
                Welcome, {username} {userType && `(${userType})`}
              </Text>
            )}
          </div>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <Empty
            description="Unable to load user access data. Please try refreshing the page."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </div>
    )
  }

  // Show message if no accessible apps 
  if (filteredCategories.length === 0) {
    return (
      <div className="home-container">
        <div className="home-header">
          <Title level={4} className="home-title">Store dashboard</Title>
          <div className="header-info">
            {username && (
              <Text className="welcome-text">
                Welcome, {username} {userType && `(${userType})`}
              </Text>
            )}
          </div>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <Empty
            description="You don't have access to any applications. Please contact your administrator."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="home-container">
      <div className="home-header">
        <Title level={4} className="home-title">Store dashboard</Title>
      </div>

      {tabItems.length > 0 && (
        <Tabs defaultActiveKey={tabItems[0]?.key} className="apps-tabs" items={tabItems} />
      )}
    </div>
  )
}

export default Home